/**
 * Roll Shade Calculator Component
 * Main pricing tool for Made.bb (Protected - requires authentication)
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { ViewList as ViewListIcon, Inventory as InventoryIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { RollShadeCalculatorService, defaultSettings } from '../services/calculatorService';
import { OrderService } from '../services/orderService';
import { OrderGenerationService } from '../services/orderGenerationService';
import type { OrderFormData, CalculationResult, Material } from '../types/madebb.types';
import { authFetch } from '../utils/authFetch';

const calculator = new RollShadeCalculatorService(defaultSettings);

export const Calculator: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [formData, setFormData] = useState<OrderFormData>({
    width: 0,
    drop: 0,
    material: '',
    colour: '',
    hardwareColour: '',
    // MADE-237: Customer details + ship/collect
    customerFirstName: '',
    customerLastName: '',
    customerCompany: '',
    customerEmailAddr: '',
    customerPhone: '',
    shipOrCollect: 'collect',
    deliveryAddress: null,
  });

  const [calculations, setCalculations] = useState<CalculationResult | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [generating, setGenerating] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [materials, setMaterials] = useState<Material[]>([]);

  // Keyboard navigation state for selects
  const [keyboardState, setKeyboardState] = useState<{
    field: string | null;
    lastKey: string | null;
    lastKeyTime: number;
    cycleIndex: number;
  }>({
    field: null,
    lastKey: null,
    lastKeyTime: 0,
    cycleIndex: 0
  });

  // Track which select dropdowns are open
  const [openSelects, setOpenSelects] = useState<{
    material: boolean;
    colour: boolean;
    hardwareColour: boolean;
  }>({
    material: false,
    colour: false,
    hardwareColour: false
  });

  // Hardware colors are static
  const hardwareColours = ['White', 'Black', 'Silver'];

  // Load materials from inventory on mount
  useEffect(() => {
    const loadMaterials = async () => {
      try {
        const response = await authFetch('/api/madebb/materials');
        const data = await response.json();
        if (data.success) {
          setMaterials(data.materials);
        }
      } catch (error) {
        console.error('Failed to load materials:', error);
      }
    };
    loadMaterials();
  }, []);

  // Extract master fabrics only (not variants)
  const masterFabrics = React.useMemo(() => {
    const masters = materials.filter(m =>
      m.type === 'fabric' &&
      m.active &&
      m.isMaster === true
    );
    return masters.map(m => ({ id: m.id, name: m.name }));
  }, [materials]);

  // Extract available colors (variants) for the selected master fabric
  const availableColours = React.useMemo(() => {
    if (!formData.material) return [];
    // Find variants where parentMaterialId matches the selected master
    const variants = materials.filter(
      m => m.type === 'fabric' &&
           m.active &&
           m.parentMaterialId === formData.material &&
           m.variantType === 'color' &&
           m.variantValue
    );
    return variants.map(v => ({ id: v.id, color: v.variantValue! }));
  }, [materials, formData.material]);

  // Reset color when material changes if the current color isn't available
  useEffect(() => {
    if (formData.material && formData.colour) {
      const colorAvailable = availableColours.some(
        c => c.id === formData.colour
      );
      if (!colorAvailable) {
        // Reset color when master changes
        setFormData(prev => ({ ...prev, colour: '' }));
      }
    }
  }, [formData.material, availableColours]);

  useEffect(() => {
    // Validate and calculate whenever form data changes
    if (formData.width && formData.drop && formData.material && formData.colour) {
      const validation = calculator.validateDimensions(formData.width, formData.drop);
      setErrors(validation.errors);

      if (validation.valid) {
        // Find the matching variant material from inventory to get its price
        const inventoryMaterial = materials.find(m => m.id === formData.colour);
        const materialCostPerYard = inventoryMaterial?.costPerYard;
        const masterMaterial = materials.find(m => m.id === formData.material);

        const result = calculator.calculateCosts(
          formData.width,
          formData.drop,
          masterMaterial?.name || formData.material,
          materialCostPerYard
        );
        setCalculations(result);
      } else {
        setCalculations(null);
      }
    }
  }, [formData, materials]);

  const handleInputChange = (
    field: keyof OrderFormData,
    value: string | number | OrderFormData['deliveryAddress']
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear success message when user makes changes
    setSuccessMessage(null);
  };

  const handleDeliveryAddressChange = (subField: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      deliveryAddress: {
        address_line_1: prev.deliveryAddress?.address_line_1 ?? '',
        address_line_2: prev.deliveryAddress?.address_line_2,
        city: prev.deliveryAddress?.city ?? '',
        province: prev.deliveryAddress?.province,
        postal_code: prev.deliveryAddress?.postal_code ?? '',
        country: prev.deliveryAddress?.country ?? '',
        [subField]: value,
      },
    }));
    setSuccessMessage(null);
  };

  /**
   * Handle keyboard navigation for select dropdowns
   * Supports arrow keys and letter keys when dropdown is CLOSED
   * When dropdown is OPEN, let Material-UI handle it normally
   */
  const handleSelectKeyDown = (
    event: React.KeyboardEvent,
    field: 'material' | 'colour' | 'hardwareColour',
    options: Array<{ id: string; display: string }>
  ) => {
    // If dropdown is open, let Material-UI handle keyboard navigation
    if (openSelects[field]) {
      return;
    }

    const key = event.key;

    // Handle arrow keys (ArrowUp/ArrowDown) when dropdown is closed
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();

      if (options.length === 0) return;

      const currentValue = formData[field];
      const currentIndex = options.findIndex(opt => opt.id === currentValue);

      let newIndex: number;
      if (key === 'ArrowDown') {
        newIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
      } else {
        newIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
      }

      handleInputChange(field, options[newIndex].id);
      return;
    }

    // Handle letter keys when dropdown is closed
    const keyLower = key.toLowerCase();
    if (key.length !== 1 || !/[a-z]/.test(keyLower)) {
      return;
    }

    // Prevent opening the dropdown and stop propagation
    event.preventDefault();
    event.stopPropagation();

    const now = Date.now();
    const timeSinceLastKey = now - keyboardState.lastKeyTime;

    // Find all options that start with the pressed key
    const matchingOptions = options.filter(opt =>
      opt.display.toLowerCase().startsWith(keyLower)
    );

    if (matchingOptions.length === 0) {
      return;
    }

    let selectedOption: typeof options[0];

    // If same key pressed within 1 second, cycle through matching options
    if (
      keyboardState.field === field &&
      keyboardState.lastKey === keyLower &&
      timeSinceLastKey < 1000
    ) {
      // Cycle to next matching option
      const nextIndex = (keyboardState.cycleIndex + 1) % matchingOptions.length;
      selectedOption = matchingOptions[nextIndex];

      setKeyboardState({
        field,
        lastKey: keyLower,
        lastKeyTime: now,
        cycleIndex: nextIndex
      });
    } else {
      // First press or timeout - select first matching option
      selectedOption = matchingOptions[0];

      setKeyboardState({
        field,
        lastKey: keyLower,
        lastKeyTime: now,
        cycleIndex: 0
      });
    }

    // Update form data with selected option ID
    handleInputChange(field, selectedOption.id);
  };

  const handleGenerateOrder = async () => {
    if (!calculations || !user) return;

    setGenerating(true);
    setErrors([]);
    setSuccessMessage(null);

    // MADE-237: Client-side customer + fulfilment validation
    const customerValidationErrors: string[] = [];
    const hasPersonalName = (formData.customerFirstName || '').trim() || (formData.customerLastName || '').trim();
    const hasCompany = (formData.customerCompany || '').trim();
    if (!hasPersonalName && !hasCompany) {
      customerValidationErrors.push('Please enter a customer name or company.');
    }
    if (formData.shipOrCollect === 'ship') {
      const addr = formData.deliveryAddress;
      if (!addr?.address_line_1?.trim() || !addr?.city?.trim() || !addr?.postal_code?.trim() || !addr?.country?.trim()) {
        customerValidationErrors.push('Please complete the delivery address.');
      }
    }
    if (customerValidationErrors.length > 0) {
      setErrors(customerValidationErrors);
      setGenerating(false);
      return;
    }

    try {
      // Get current settings to get order number
      const settings = await OrderService.getSettings();

      // Create order in backend
      const masterMaterial = materials.find(m => m.id === formData.material);
      const variantMaterial = materials.find(m => m.id === formData.colour);

      const orderData = {
        dimensions: {
          width: formData.width,
          drop: formData.drop
        },
        material: {
          id: formData.colour, // Use variant ID
          name: masterMaterial?.name || '',
          color: variantMaterial?.variantValue || ''
        },
        hardwareColor: formData.hardwareColour,
        fabricYardage: calculations.fabricYardage,
        pulleyCount: calculations.pulleyCount,
        pullCordLength: calculations.pullCordLength,
        costs: {
          fabricCost: calculations.fabricCost,
          pineCost: calculations.pineCost,
          bottomPipeCost: calculations.bottomPipeCost,
          laborCost: calculations.laborCost,
          totalLaborHours: calculations.totalLaborHours,
          productionCost: calculations.productionCost,
          bulkRate: calculations.bulkRate,
          retailPrice: calculations.price
        },
        production: {
          assignedTo: null,
          startedAt: null,
          completedAt: null,
          qualityCheck: {
            inspector: null,
            passedAt: null,
            notes: [],
            widthMatch: null,
            dropMatch: null,
            colorVerified: null,
            hardwareColorVerified: null,
            componentsIncluded: null,
            assemblyQuality: null
          }
        },
        createdBy: user.email || 'unknown',
        // MADE-237: Customer details + ship/collect
        customerFirstName: formData.customerFirstName || null,
        customerLastName: formData.customerLastName || null,
        customerCompany: formData.customerCompany || null,
        customerEmailAddr: formData.customerEmailAddr || null,
        customerPhone: formData.customerPhone || null,
        shipOrCollect: formData.shipOrCollect || 'collect',
        deliveryAddress: formData.shipOrCollect === 'ship' ? formData.deliveryAddress : null,
      };

      const savedOrder = await OrderService.createOrder(orderData);

      // Add order to production queue
      try {
        const queueResponse = await authFetch('/api/madebb/production-queue', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            itemType: 'order',
            referenceId: savedOrder.orderId,
            quantity: 1
          })
        });
        const queueData = await queueResponse.json();
        if (!queueData.success) {
          console.warn('Failed to add order to production queue:', queueData.error);
        } else {
          console.log(`✅ Order ${savedOrder.orderId} added to production queue`);
          if (queueData.cascadedItems && queueData.cascadedItems.length > 0) {
            console.log(`   ${queueData.cascadedItems.length} dependencies auto-added to queue`);
          }
        }
      } catch (queueError) {
        console.warn('Failed to add order to production queue:', queueError);
        // Don't fail the order creation, just log the warning
      }

      // Deduct inventory for the order
      try {
        const deductions = [
          {
            materialId: formData.colour, // Use variant ID directly
            quantity: calculations.fabricYardage
          }
          // Additional materials (hardware, pine, pipe, etc.) would be deducted here
          // TODO: Add hardware inventory deductions based on pulleyCount, etc.
        ];

        await authFetch('/api/madebb/inventory/deduct', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            orderId: savedOrder.orderId,
            deductions
          })
        });
      } catch (inventoryError) {
        console.warn('Inventory deduction failed:', inventoryError);
        // Don't fail the order creation, just log the warning
        // Inventory can be manually adjusted if needed
      }

      // Generate printable order form with security token
      await OrderGenerationService.generateOrderForm(
        formData,
        calculations,
        savedOrder.orderNumber,
        savedOrder.securityToken
      );

      setSuccessMessage(`Order ${savedOrder.orderId} created successfully! Print window opened.`);

      // Reset form fields to prevent duplicates
      setFormData({
        width: 0,
        drop: 0,
        material: '',
        colour: '',
        hardwareColour: '',
        customerFirstName: '',
        customerLastName: '',
        customerCompany: '',
        customerEmailAddr: '',
        customerPhone: '',
        shipOrCollect: 'collect',
        deliveryAddress: null,
      });
      setCalculations(null);
    } catch (error: any) {
      console.error('Error generating order:', error);
      setErrors([error.message || 'Failed to generate order. Please try again.']);
    } finally {
      setGenerating(false);
    }
  };

  const isFormComplete = formData.width && formData.drop && formData.material &&
    formData.colour && formData.hardwareColour && calculations && errors.length === 0;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: { xs: 2, md: 4 }
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Paper
          elevation={4}
          sx={{
            padding: 3,
            marginBottom: 3,
            background: 'linear-gradient(45deg, #2c3e50, #34495e)',
            color: 'white',
            position: 'relative'
          }}
        >
          {/* Header is now in MadebbLayout */}

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
              Made.bb Roll Shades
            </Typography>
            <Typography variant="h6">
              Custom Order Form
            </Typography>
            {user && (
              <Typography variant="body2" sx={{ marginTop: 1, opacity: 0.8 }}>
                Salesperson: {user.email}
              </Typography>
            )}
          </Box>
        </Paper>

        <Paper elevation={8} sx={{ padding: { xs: 3, md: 5 }, borderRadius: 3 }}>
          {/* Dimension Inputs */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Width (inches)"
                type="number"
                value={formData.width || ''}
                onChange={(e) => handleInputChange('width', parseFloat(e.target.value))}
                inputProps={{ min: 18, max: 120, step: 0.25 }}
                required
                error={errors.some(e => e.includes('Width'))}
                helperText={errors.find(e => e.includes('Width'))}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Drop (inches)"
                type="number"
                value={formData.drop || ''}
                onChange={(e) => handleInputChange('drop', parseFloat(e.target.value))}
                inputProps={{ min: 8, max: 120, step: 0.25 }}
                required
                error={errors.some(e => e.includes('Drop'))}
                helperText={errors.find(e => e.includes('Drop'))}
              />
            </Grid>

            {/* Material Selection (Master Fabrics Only) */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth required>
                <InputLabel>Material</InputLabel>
                <Select
                  value={formData.material}
                  onChange={(e) => handleInputChange('material', e.target.value)}
                  onKeyDown={(e) => handleSelectKeyDown(e, 'material', masterFabrics.map(m => ({ id: m.id, display: m.name })))}
                  onOpen={() => setOpenSelects(prev => ({ ...prev, material: true }))}
                  onClose={() => setOpenSelects(prev => ({ ...prev, material: false }))}
                  label="Material"
                  SelectDisplayProps={{
                    onKeyDown: (e) => handleSelectKeyDown(e as any, 'material', masterFabrics.map(m => ({ id: m.id, display: m.name })))
                  }}
                >
                  <MenuItem value="">Select Material</MenuItem>
                  {masterFabrics.map(mat => (
                    <MenuItem key={mat.id} value={mat.id}>{mat.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Colour Selection (Variants of Selected Master) */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth required disabled={!formData.material || availableColours.length === 0}>
                <InputLabel>Colour</InputLabel>
                <Select
                  value={formData.colour}
                  onChange={(e) => handleInputChange('colour', e.target.value)}
                  onKeyDown={(e) => handleSelectKeyDown(e, 'colour', availableColours.map(c => ({ id: c.id, display: c.color })))}
                  onOpen={() => setOpenSelects(prev => ({ ...prev, colour: true }))}
                  onClose={() => setOpenSelects(prev => ({ ...prev, colour: false }))}
                  label="Colour"
                  SelectDisplayProps={{
                    onKeyDown: (e) => handleSelectKeyDown(e as any, 'colour', availableColours.map(c => ({ id: c.id, display: c.color })))
                  }}
                >
                  <MenuItem value="">Select Colour</MenuItem>
                  {availableColours.map(variant => (
                    <MenuItem key={variant.id} value={variant.id}>{variant.color}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Hardware Colour Selection */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth required>
                <InputLabel>Hardware Colour</InputLabel>
                <Select
                  value={formData.hardwareColour}
                  onChange={(e) => handleInputChange('hardwareColour', e.target.value)}
                  onKeyDown={(e) => handleSelectKeyDown(e, 'hardwareColour', hardwareColours.map(c => ({ id: c.toLowerCase(), display: c })))}
                  onOpen={() => setOpenSelects(prev => ({ ...prev, hardwareColour: true }))}
                  onClose={() => setOpenSelects(prev => ({ ...prev, hardwareColour: false }))}
                  label="Hardware Colour"
                  SelectDisplayProps={{
                    onKeyDown: (e) => handleSelectKeyDown(e as any, 'hardwareColour', hardwareColours.map(c => ({ id: c.toLowerCase(), display: c })))
                  }}
                >
                  <MenuItem value="">Select Hardware Colour</MenuItem>
                  {hardwareColours.map(col => (
                    <MenuItem key={col} value={col.toLowerCase()}>{col}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* MADE-237: Customer Details */}
          <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>Customer Details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="First Name"
                value={formData.customerFirstName || ''}
                onChange={(e) => handleInputChange('customerFirstName', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Last Name"
                value={formData.customerLastName || ''}
                onChange={(e) => handleInputChange('customerLastName', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Company Name"
                value={formData.customerCompany || ''}
                onChange={(e) => handleInputChange('customerCompany', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                type="email"
                value={formData.customerEmailAddr || ''}
                onChange={(e) => handleInputChange('customerEmailAddr', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Phone"
                value={formData.customerPhone || ''}
                onChange={(e) => handleInputChange('customerPhone', e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>

          {/* MADE-237: Fulfilment selector */}
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>Fulfilment</Typography>
          <ToggleButtonGroup
            value={formData.shipOrCollect || 'collect'}
            exclusive
            onChange={(_, val) => val && handleInputChange('shipOrCollect', val)}
          >
            <ToggleButton value="collect">Collect</ToggleButton>
            <ToggleButton value="ship">Ship</ToggleButton>
          </ToggleButtonGroup>

          {/* MADE-237: Conditional delivery address sub-form */}
          {formData.shipOrCollect === 'ship' && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  label="Address Line 1"
                  required
                  value={formData.deliveryAddress?.address_line_1 || ''}
                  onChange={(e) => handleDeliveryAddressChange('address_line_1', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Address Line 2"
                  value={formData.deliveryAddress?.address_line_2 || ''}
                  onChange={(e) => handleDeliveryAddressChange('address_line_2', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="City"
                  required
                  value={formData.deliveryAddress?.city || ''}
                  onChange={(e) => handleDeliveryAddressChange('city', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Province / State"
                  value={formData.deliveryAddress?.province || ''}
                  onChange={(e) => handleDeliveryAddressChange('province', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Postal Code"
                  required
                  value={formData.deliveryAddress?.postal_code || ''}
                  onChange={(e) => handleDeliveryAddressChange('postal_code', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Country"
                  required
                  value={formData.deliveryAddress?.country || ''}
                  onChange={(e) => handleDeliveryAddressChange('country', e.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>
          )}

          {/* Calculation Results */}
          {calculations && (
            <Box
              sx={{
                marginTop: 4,
                padding: 3,
                background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)',
                borderRadius: 2
              }}
            >
              <Typography variant="h5" sx={{ marginBottom: 3, fontWeight: 'bold', color: '#2c3e50' }}>
                Order Summary
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: 2, borderBottom: '1px solid #dee2e6' }}>
                    <Typography fontWeight="bold" color="#495057">Dimensions:</Typography>
                    <Typography fontWeight="bold" color="#2c3e50">
                      {formData.width}" W × {formData.drop}" D
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: 2, borderBottom: '1px solid #dee2e6' }}>
                    <Typography fontWeight="bold" color="#495057">Material:</Typography>
                    <Typography fontWeight="bold" color="#2c3e50">
                      {materials.find(m => m.id === formData.material)?.name || formData.material}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: 2, borderBottom: '1px solid #dee2e6' }}>
                    <Typography fontWeight="bold" color="#495057">Colour:</Typography>
                    <Typography fontWeight="bold" color="#2c3e50">
                      {materials.find(m => m.id === formData.colour)?.variantValue || formData.colour}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: 2, borderBottom: '1px solid #dee2e6' }}>
                    <Typography fontWeight="bold" color="#495057">Fabric Yardage:</Typography>
                    <Typography fontWeight="bold" color="#2c3e50">
                      {calculations.fabricYardage} yards
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ marginY: 2 }} />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: 2 }}>
                    <Typography fontWeight="bold" color="#495057">Production Cost:</Typography>
                    <Typography fontWeight="bold" color="#2c3e50" fontSize="1.2rem">
                      ${calculations.productionCost.toFixed(2)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: 2 }}>
                    <Typography fontWeight="bold" color="#495057">Bulk Rate:</Typography>
                    <Typography fontWeight="bold" color="#2c3e50" fontSize="1.2rem">
                      ${calculations.bulkRate.toFixed(2)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: 2, background: '#d4edda', borderRadius: 1 }}>
                    <Typography fontWeight="bold" color="#155724">Retail Price:</Typography>
                    <Typography fontWeight="bold" color="#28a745" fontSize="1.5rem">
                      ${calculations.price.toFixed(2)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Success Message */}
          {successMessage && (
            <Alert severity="success" sx={{ marginTop: 3 }}>
              {successMessage}
            </Alert>
          )}

          {/* Error Display */}
          {errors.length > 0 && (
            <Alert severity="error" sx={{ marginTop: 3 }}>
              {errors.map((err, idx) => (
                <div key={idx}>{err}</div>
              ))}
            </Alert>
          )}

          {/* Generate Order Button */}
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleGenerateOrder}
            disabled={!isFormComplete || generating}
            sx={{
              marginTop: 4,
              padding: '15px',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #667eea, #764ba2)',
              '&:hover': {
                background: 'linear-gradient(45deg, #5568d3, #653a8b)',
                transform: 'translateY(-2px)',
                boxShadow: '0 10px 20px rgba(0,0,0,0.2)'
              },
              '&:disabled': {
                background: '#6c757d'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {generating ? 'Generating Order...' : 'Generate Order Form'}
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};
